import {
	createAsync,
	Navigate,
	useAction,
	useLocation,
	useNavigate,
	useSubmission,
	useSubmissions,
} from '@solidjs/router';
import { Title } from '@solidjs/meta';
import { createEffect, createSignal, For, Match, on, Show, Suspense, Switch } from 'solid-js';
import {
	ActivityIndicator,
	Button,
	CardDetails,
	Container,
	CheckoutStep,
	createCheckoutStepFlow,
	Errors,
	Form,
	Grid,
	GridFive,
	GridSeven,
	Heading,
	Link,
	Radio,
	Receipt,
	Section,
	TextLink,
} from '@troon/ui';
import { clientAction, objectToFormData, revalidate } from '@troon/api-client';
import { PaymentMethodSelector } from '@troon/payments';
import { useTrackEvent } from '@troon/analytics';
import { IconCircleWarning } from '@troon/icons/circle-warning';
import { IconCircleCheckCustom } from '@troon/icons/circle-check-custom';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import dayjs from '@troon/dayjs';
import { twJoin } from '@troon/tailwind-preset/merge';
import { getConfigValue } from '../../../../modules/config';
import { usePersisted } from '../../../../providers/persistence-store';
import { getAccessProducts, useAccessProductsQuery } from '../../../../providers/card';
import { useUser } from '../../../../providers/user';
import { PromoCodeEntry } from '../../../../components/promo-code-entry';
import { AccessProductRadioItem } from '../../../../components/access-product-radio-item';
import { productName } from '../../../../modules/access-products';
import {
	canUpgradeToProduct,
	getAvailableUpgradeProducts,
	getUpgradeProductType,
	useAccessPremiumEliteEnabled,
} from '../../../../modules/access-upgrades';
import type { AccessProduct, UpgradeAccessProduct, UpgradeProductType } from '../../../../modules/access-upgrades';
import type { ApiResponse } from '@troon/api-client';
import type { RouteDefinition, RouteSectionProps } from '@solidjs/router';

const upgradePreviewPath = '/v0/troon-access-subscriptions/upgrade/preview';
const previewUpgradeAction = clientAction('post', upgradePreviewPath);
const artificialTimeoutMs = 2_000;
const confirmUpgradeAction = clientAction('post', '/v0/troon-access-subscriptions/upgrade/confirm');

type UpgradePreviewResponse = ApiResponse<'post', typeof upgradePreviewPath>;
type UpgradeConfirmResponse = ApiResponse<'post', '/v0/troon-access-subscriptions/upgrade/confirm'>;
type CreditCard = ApiResponse<'post', '/v1/payments/credit-cards'>;
type UpgradeSuccess = { product: UpgradeAccessProduct; response: UpgradeConfirmResponse };
type UpgradeStep = 'choose' | 'payment';

const upgradeStepOrder: Array<UpgradeStep> = ['choose', 'payment'];
const stepFlow = 'Upgrade Your Access';
const stepOptions = { noDuplicates: true };

export default function AccessUpgradePage(props: RouteSectionProps) {
	const user = useUser();
	const navigate = useNavigate();
	const location = useLocation<{ redirect?: string }>();
	const [persisted] = usePersisted();
	const accessPremiumEliteEnabled = useAccessPremiumEliteEnabled();
	const targetProductType = () => getUpgradeProductType(props.params.product);
	const redirectHref = () => location.state?.redirect ?? '/account/subscription';
	const [upgradeSuccess, setUpgradeSuccess] = createSignal<UpgradeSuccess>();
	const accessProductsQuery = useAccessProductsQuery(() => persisted.promoCode?.value);
	const products = createAsync(() => getAccessProducts({ query: accessProductsQuery() }), { deferStream: true });
	const currentProductType = () => user()?.subscription?.productType ?? user()?.troonAccessProductType;
	const shouldRedirectPremiumPlusToElite = () =>
		currentProductType() === 'TROON_ACCESS_PLUS' && targetProductType() === 'TROON_ACCESS_PLUS';
	const availableUpgradeProducts = () => {
		const current = currentProductType();
		return getAvailableUpgradeProducts(current, products()?.products);
	};
	const targetProduct = () => availableUpgradeProducts()?.find((product) => product.type === targetProductType());
	const checkoutOptions = () => {
		const availableProducts = availableUpgradeProducts();
		const initialProduct = targetProduct();

		return availableProducts?.length && initialProduct ? { availableProducts, initialProduct } : undefined;
	};

	return (
		<Switch>
			<Match when={!user()}>
				<Navigate href="/membership" />
			</Match>
			<Match when={!accessPremiumEliteEnabled()}>
				<Navigate href={redirectHref()} />
			</Match>
			<Match
				when={
					!user()?.troonAccessProductType ||
					!user()?.subscription ||
					user()?.subscription?.status !== 'ACTIVE' ||
					!targetProductType()
				}
			>
				<Navigate href="/membership" />
			</Match>
			<Match when={shouldRedirectPremiumPlusToElite()}>
				<Navigate href="/membership/upgrade/elite" />
			</Match>
			<Match when={!upgradeSuccess() && !canUpgradeToProduct(currentProductType(), targetProductType())}>
				<UpgradeUnavailableNotice currentProductType={currentProductType()} targetProductType={targetProductType()} />
			</Match>
			<Match when={true}>
				<Container size="default" class="py-4 md:py-10">
					<Title>Upgrade Access | Golf with Access</Title>
					<Suspense fallback={<UpgradePageSkeleton />}>
						<Switch>
							<Match when={upgradeSuccess()}>
								{(success) => (
									<UpgradeConfirmation
										product={success().product}
										response={success().response}
										userEmail={user()?.email}
										onClose={() => navigate(redirectHref())}
									/>
								)}
							</Match>
							<Match when={checkoutOptions()}>
								{(options) => (
									<UpgradeCheckoutFlow
										products={options().availableProducts}
										initialProduct={options().initialProduct}
										onUpgradeSuccess={setUpgradeSuccess}
									/>
								)}
							</Match>
							<Match when={true}>
								<UpgradeUnavailableNotice
									currentProductType={currentProductType()}
									targetProductType={targetProductType()}
								/>
							</Match>
						</Switch>
					</Suspense>
				</Container>
			</Match>
		</Switch>
	);
}

function UpgradeCheckoutFlow(props: {
	products: Array<UpgradeAccessProduct>;
	initialProduct: UpgradeAccessProduct;
	onUpgradeSuccess: (data: UpgradeSuccess) => void;
}) {
	const trackEvent = useTrackEvent();
	const { complete, edit, getIndex, stepState, store } = createCheckoutStepFlow<UpgradeStep>({
		current: 'choose',
		order: upgradeStepOrder,
		stepFlow,
		onStepCompleted: ({ stepFlow, stepName, stepSkipped }) => {
			trackEvent('stepCompleted', { stepFlow, stepName, stepSkipped }, stepOptions);
		},
		steps: {
			choose: { complete: false, skipped: false },
			payment: { complete: false, skipped: false },
		},
	});
	const [selectedProductType, setSelectedProductType] = createSignal<UpgradeProductType>(props.initialProduct.type);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = createSignal<string>();
	const [selectedCard, setSelectedCard] = createSignal<CreditCard>();
	const [isConfirmingMembership, setIsConfirmingMembership] = createSignal(false);
	const fetchPreviewInvoice = useAction(previewUpgradeAction);
	const upgradeConfirmation = useSubmission(confirmUpgradeAction);
	const submissions = useSubmissions(previewUpgradeAction);
	const selectedProduct = () => {
		const productType = selectedProductType();
		return props.products.find((product) => product.type === productType) ?? props.initialProduct;
	};
	const latestPreview = () => {
		const productType = selectedProductType();
		return submissions.findLast(
			(submission) => !submission.pending && !submission.error && submission.result?.toProductType === productType,
		)?.result;
	};
	const previewPending = () => submissions.some((submission) => submission.pending);
	const canSubmitUpgrade = () =>
		store.steps.choose.complete && !!selectedPaymentMethod() && !!latestPreview() && !previewPending();

	createEffect(
		on(selectedProductType, (productType) => {
			fetchPreviewInvoice(
				objectToFormData({ targetProductType: productType, promoCode: latestPreview()?.promoCode ?? '' }),
			);
		}),
	);

	createEffect(
		on(
			() => upgradeConfirmation.result,
			(response) => {
				if (!response) {
					return;
				}
				const product = selectedProduct();
				const value = parseFloat(latestPreview()?.amountDueToday.value ?? '0');
				setIsConfirmingMembership(true);
				trackEvent('checkout', {
					productType: 'troonAccessUpgrade',
					productName: product.subscriptionName,
					value,
				});
				void (async () => {
					await new Promise<void>((resolve) => {
						setTimeout(() => resolve(), artificialTimeoutMs);
					});
					props.onUpgradeSuccess({ product, response });
					void revalidate('/v0/auth/me', true);
				})();
			},
		),
	);

	return (
		<Show
			when={!isConfirmingMembership()}
			fallback={
				<div class="grid min-h-[50dvh] place-items-center">
					<ActivityIndicator class="size-16 text-brand">Confirming your Membership</ActivityIndicator>
				</div>
			}
		>
			<div class="flex flex-col gap-6">
				<Heading as="h1" size="h2">
					Upgrade Access
				</Heading>
				<Grid>
					<GridSeven class="flex flex-col gap-4">
						<CheckoutStep
							state={stepState('choose')}
							step={getIndex('choose')}
							title="Choose your Access"
							onEdit={() => edit('choose')}
							value={selectedProduct().subscriptionName}
						>
							<Radio.Group
								name="targetProductType"
								class="gap-4"
								value={selectedProductType()}
								onValueChange={(change) => {
									const product = props.products.find((product) => product.type === change.value);
									if (product) {
										setSelectedProductType(product.type);
									}
								}}
							>
								<Radio.GroupLabel class="sr-only">Choose your Access</Radio.GroupLabel>
								<For each={props.products}>
									{(product) => (
										<AccessProductRadioItem product={product} value={product.type} description={product.description} />
									)}
								</For>
							</Radio.Group>
							<Button onClick={complete}>Continue to Payment</Button>
						</CheckoutStep>

						<CheckoutStep
							state={stepState('payment')}
							step={getIndex('payment')}
							title="Payment Information"
							value={
								<Show when={selectedCard()}>
									{(card) => <CardDetails brand={card().brand} lastFour={card().lastFour} />}
								</Show>
							}
						>
							<Suspense fallback={<ActivityIndicator />}>
								<PaymentMethodSelector
									processor="STRIPE"
									stripePublishableKey={getConfigValue('STRIPE_PUBLISHABLE_KEY')}
									selected={selectedPaymentMethod()}
									onSelect={(card) => {
										setSelectedPaymentMethod(card.id);
										setSelectedCard(card);
									}}
									labelHidden
									hideSelectButton
									wallets={{ applePay: 'never', googlePay: 'never', link: 'never' }}
								/>
							</Suspense>
						</CheckoutStep>
					</GridSeven>

					<GridFive>
						<div class="flex flex-col gap-4 md:sticky md:top-4 md:max-h-[calc(100dvh-2rem)] md:overflow-y-auto">
							<Section appearance="contained">
								<Heading as="h2" size="h4">
									Summary
								</Heading>
								<PromoCodeEntry
									promoCode={latestPreview()?.promoCode ?? undefined}
									action={previewUpgradeAction}
									intentData={{
										targetProductType: selectedProductType(),
									}}
								/>
								<Switch>
									<Match when={previewPending() || !latestPreview()}>
										<UpgradePreviewSkeleton />
									</Match>
									<Match when={latestPreview()}>{(preview) => <UpgradePreview preview={preview()} />}</Match>
								</Switch>
								<Form action={confirmUpgradeAction} method="post">
									<input type="hidden" name="targetProductType" value={selectedProductType()} />
									<input type="hidden" name="paymentMethodId" value={selectedPaymentMethod()} />
									<input type="hidden" name="promoCode" value={latestPreview()?.promoCode ?? ''} />
									<Errors />
									<Button
										pending={upgradeConfirmation.pending}
										disabled={!canSubmitUpgrade() || upgradeConfirmation.pending}
									>
										Upgrade to {selectedProduct().subscriptionName}
										<IconArrowRightMd />
									</Button>
								</Form>
							</Section>
							<p class="text-center text-sm text-balance text-neutral-700">
								By clicking "Upgrade", you are agreeing to Troon's{' '}
								<TextLink target="_blank" href="/terms">
									Terms of Service
								</TextLink>{' '}
								and{' '}
								<TextLink target="_blank" href={`https://${getConfigValue('HOST')}/privacy-policy`}>
									Privacy Policy
								</TextLink>
								.
							</p>
						</div>
					</GridFive>
				</Grid>
			</div>
		</Show>
	);
}

function UpgradePreview(props: { preview: UpgradePreviewResponse }) {
	return (
		<Receipt
			items={props.preview.items.map((item) => ({
				label: item.description
					? `${item.name ?? 'Membership upgrade'}\n${item.description}`
					: (item.name ?? 'Membership upgrade'),
				amount: <span class={twJoin('font-semibold', item.price < 0 && 'text-green-600')}>{item.priceDisplay}</span>,
			}))}
			totals={[{ label: 'Total Due Now', amount: props.preview.amountDueToday.displayValue }]}
			totalDisclaimer={props.preview.renewalMessage}
		/>
	);
}

function UpgradeConfirmation(props: {
	product: UpgradeAccessProduct;
	response: UpgradeConfirmResponse;
	userEmail: string | undefined;
	onClose: () => void;
}) {
	const subscription = () => props.response.stripeSubscription;
	const renewalMessage = () => {
		if (subscription().nextInvoiceDate) {
			return `Your membership will renew automatically on ${dayjs(subscription().nextInvoiceDate!.epoch)
				.tz(subscription().nextInvoiceDate!.tz)
				.format('MM/DD/YYYY')}.`;
		}
		if (subscription().endDate) {
			return `Your membership will not auto-renew and will end on ${dayjs(subscription().endDate!.epoch).format('MM/DD/YYYY')}.`;
		}
		return '';
	};

	return (
		<div class="mx-auto flex max-w-2xl flex-col gap-8">
			<div class="flex flex-col items-center gap-6 text-center">
				<IconCircleCheckCustom class="text-8xl" />
				<Heading as="h1" size="h3">
					Your {props.product.subscriptionName} membership is active!
				</Heading>
				<p class="text-lg">We’ve sent a confirmation email to {props.userEmail}.</p>
			</div>
			<div class="flex flex-col gap-4 rounded-lg border border-neutral p-6">
				<Heading as="h2" size="h4">
					Order summary
				</Heading>
				<Receipt
					items={[
						{
							label: 'Subtotal',
							amount: subscription().subtotalAmount.displayValue,
						},
						...(parseFloat(subscription().discountAmount.value)
							? [
									{
										label: 'Discount',
										amount: <span class="text-green-600">{subscription().discountAmount.displayValue}</span>,
									},
								]
							: []),
						{
							label: 'Tax',
							amount: subscription().taxAmount.displayValue,
						},
					]}
					totals={[{ label: 'Total', amount: subscription().totalAmount.displayValue }]}
					totalDisclaimer={renewalMessage()}
				/>
			</div>
			<Button onClick={props.onClose}>Go to account</Button>
		</div>
	);
}

function UpgradeUnavailableNotice(props: {
	currentProductType?: AccessProduct['type'] | null;
	targetProductType?: UpgradeProductType;
}) {
	const alreadyOnTarget = () => props.currentProductType === props.targetProductType;
	const targetMembershipName = () =>
		props.targetProductType ? `Access ${productName[props.targetProductType]}` : 'this membership';
	const heading = () => (alreadyOnTarget() ? "You're already on this membership" : "This upgrade isn't available");
	const message = () =>
		alreadyOnTarget()
			? `Your account is already on ${targetMembershipName()}. You can review billing, renewal, and plan details from your subscription page.`
			: `Your current membership cannot be changed to ${targetMembershipName()} from this page. You can manage your subscription from your account page.`;

	return (
		<Container class="py-4 md:py-12">
			<Title>Upgrade Your Access | Golf with Access</Title>
			<Section appearance="contained" class="max-w-2xl gap-4 p-4 md:gap-5 md:p-8">
				<div class="flex items-start gap-3 md:items-center md:gap-4">
					<div class="flex size-9 shrink-0 items-center justify-center text-brand md:size-12">
						<Show when={alreadyOnTarget()} fallback={<IconCircleWarning class="size-8 md:size-10" />}>
							<IconCircleCheckCustom class="size-8 md:size-10" />
						</Show>
					</div>
					<Heading as="h1" size="h5">
						{heading()}
					</Heading>
				</div>
				<p class="text-sm leading-6 text-neutral-700 md:text-base">{message()}</p>
				<Button as={Link} href="/account/subscription" appearance="secondary" size="sm" class="sm:w-fit">
					View subscription
				</Button>
			</Section>
		</Container>
	);
}

function UpgradePageSkeleton() {
	return (
		<Grid>
			<GridSeven class="flex flex-col gap-6">
				<div class="h-10 w-1/2 animate-pulse rounded-lg bg-neutral-200" />
				<UpgradePreviewSkeleton />
			</GridSeven>
			<GridFive>
				<div class="h-80 animate-pulse rounded-lg bg-neutral-200" />
			</GridFive>
		</Grid>
	);
}

function UpgradePreviewSkeleton() {
	return (
		<div class="flex flex-col gap-4">
			<div class="h-12 w-full animate-pulse rounded-lg bg-neutral-200" />
			<div class="h-8 w-2/3 animate-pulse rounded-lg bg-neutral-200" />
			<div class="h-8 w-full animate-pulse rounded-lg bg-neutral-200" />
		</div>
	);
}

export const route = {
	info: { nav: { appearance: 'extra-minimal', sticky: false }, banner: { hide: true } },
} satisfies RouteDefinition;
